import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import {
  api, ErroreApi, gestisciSessioneScaduta, impostaSolaLettura, type Cataloghi, type Io, type Paese, type Tassonomia,
} from './api.ts'
import * as registrazione from './registrazione.ts'
import { attivaNotifichePush, preparaAppNativa, scollegaTelefono } from './app-nativa.ts'
import { avviaNovita } from './avvisi.ts'
import { inOrdineDiSede } from './sedi.ts'
import {
  modoTelefono, passkeyDisponibile, propostaComputerRimandata, ricordaApp, rimandaPropostaComputer, riprendiApp,
  segnaComputerRicordato, sfidaSblocco, suComputer, type ModoTelefono, type OpzioniSblocco,
} from './accesso-ricordato.ts'

/** Sede attiva per le tendine: gli sportelli portano la sede madre (463). */
export type SedeSessione = {
  id: string; nome: string; codice: string; sede_padre_id: string | null; sede_padre_nome: string | null
}

/**
 * Sessione, cataloghi e conteggio delle notifiche.
 * I permessi arrivano dall'API: l'interfaccia li usa solo per non mostrare comandi inutili.
 * La decisione vera resta sempre del server, che non si fida di quello che dice il browser.
 */
type Contesto = {
  io: Io | null
  caricamento: boolean
  tassonomia: Tassonomia | null
  cataloghi: Cataloghi | null
  paesi: Paese[]
  /** Sedi attive, ogni sede seguita dai suoi sportelli (servizi/sedi.ts). */
  sedi: SedeSessione[]
  notificheDaLeggere: number
  /** Non nullo solo se una registrazione dello schermo sta girando adesso. */
  avvisoRegistrazione: string | null
  /** Come sta registrando adesso, se sta registrando. */
  modalitaRegistrazione: 'continua' | 'assistenza' | null
  /** Vero se l'operatore può avviare da sé una registrazione per farsi assistere. */
  puoRegistrarePerAssistenza: boolean
  registrazionePerAssistenza: (accendi: boolean) => Promise<void>
  puo: (permesso: string) => boolean
  /** Rilegge i cataloghi dopo l'aggiunta di una voce, perche' i moduli la propongano subito. */
  ricaricaCataloghi: () => Promise<void>
  aggiorna: () => Promise<void>
  aggiornaNotifiche: () => Promise<void>
  esci: () => Promise<void>
  /** App Android, web app dalla schermata Home o browser (null): servizi/accesso-ricordato.ts (521). */
  modoTelefono: ModoTelefono | null
  /** Web app bloccata: la sfida per Face ID, oppure null mentre arriva. */
  bloccato: boolean
  sblocco: OpzioniSblocco | null
  sbloccato: () => Promise<void>
  entraConPassword: () => void
  /** Web app non ancora ricordata: proposta di «Resta connesso» con Face ID. */
  propostaFaceId: boolean
  chiudiPropostaFaceId: () => void
  /** Browser di computer non ancora ricordato (531): proposta di «Ricordami su questo computer». */
  propostaComputer: boolean
  /** Chiude la proposta; con `rimanda` non torna per 30 giorni in questo browser. */
  chiudiPropostaComputer: (rimanda: boolean) => void
}

const ContestoSessione = createContext<Contesto | null>(null)

const OGNI_MINUTO = 60_000

/** Web app dell'iPhone: dopo cinque minuti in secondo piano si torna con Face ID (521), come nell'app. */
const BLOCCO_WEB_APP = 5 * 60_000
const PROPOSTA_CHIUSA = 'cidis-proposta-face-id-chiusa'

/**
 * Dopo quanto silenzio una scheda aperta smette di interrogare il server.
 *
 * Su Serverless SQL ogni richiesta riaccende il database per una finestra minima di cinque
 * minuti. Una scheda dimenticata aperta il venerdi' sera interrogava le notifiche ogni minuto
 * per tutto il fine settimana: costo pieno, e nessuno a guardare. Trenta minuti e' anche la
 * soglia oltre la quale nessuno sta davvero lavorando a quella pagina (SIC-25).
 */
const SILENZIO_MASSIMO = 30 * 60_000

const EVENTI_DI_INTERAZIONE = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const

/**
 * Un'azione ripetuta a intervalli, ma solo mentre qualcuno guarda.
 *
 * Il temporizzatore si ferma quando la pagina e' nascosta o quando l'ultima interazione e'
 * piu' vecchia di mezz'ora, e riparte — con un'esecuzione immediata, per recuperare quello
 * che e' successo nel frattempo — appena la pagina torna in primo piano o l'operatore tocca
 * qualcosa. Le rotte cosi' interrogate sono dichiarate in apps/api/src/plugin/sessione.ts e
 * non rinnovano la sessione: e' l'inattivita' della persona che deve farla scadere, non quella
 * del temporizzatore.
 *
 * `azione` viene riletta a ogni giro da un riferimento, cosi' una funzione ricreata a ogni
 * render non riavvia il temporizzatore.
 */
export function useIntervalloAttivo(azione: () => void, periodo: number, acceso = true) {
  const ultima = useRef(azione)
  ultima.current = azione

  useEffect(() => {
    if (!acceso) return
    let ultimaInterazione = Date.now()
    let timer: number | null = null

    const attiva = () => !document.hidden && Date.now() - ultimaInterazione < SILENZIO_MASSIMO

    const ferma = () => {
      if (timer !== null) { window.clearInterval(timer); timer = null }
    }
    const riparti = (subito: boolean) => {
      if (!attiva()) { ferma(); return }
      if (subito) ultima.current()
      if (timer === null) {
        timer = window.setInterval(() => {
          // Il controllo si ripete anche dentro il giro: la mezz'ora puo' scadere mentre la
          // pagina e' davanti agli occhi di nessuno, senza che arrivi nessun evento.
          if (!attiva()) { ferma(); return }
          ultima.current()
        }, periodo)
      }
    }

    const alVisibile = () => { riparti(!document.hidden) }
    const alTocco = () => {
      const eraFermo = timer === null
      ultimaInterazione = Date.now()
      // Solo la ripresa dopo una pausa rilegge subito: un movimento del mouse a temporizzatore
      // acceso non deve moltiplicare le richieste.
      if (eraFermo) riparti(true)
    }

    riparti(true)
    document.addEventListener('visibilitychange', alVisibile)
    for (const e of EVENTI_DI_INTERAZIONE) window.addEventListener(e, alTocco, { passive: true })
    return () => {
      ferma()
      document.removeEventListener('visibilitychange', alVisibile)
      for (const e of EVENTI_DI_INTERAZIONE) window.removeEventListener(e, alTocco)
    }
  }, [periodo, acceso])
}

export function FornitoreSessione({ children }: { children: ReactNode }) {
  const [io, setIo] = useState<Io | null>(null)
  const [caricamento, setCaricamento] = useState(true)
  const [tassonomia, setTassonomia] = useState<Tassonomia | null>(null)
  const [cataloghi, setCataloghi] = useState<Cataloghi | null>(null)
  const [paesi, setPaesi] = useState<Paese[]>([])
  const [sedi, setSedi] = useState<SedeSessione[]>([])
  const [notificheDaLeggere, setNotificheDaLeggere] = useState(0)
  const [avvisoRegistrazione, setAvvisoRegistrazione] = useState<string | null>(null)
  const [puoRegistrarePerAssistenza, setPuoRegistrarePerAssistenza] = useState(false)
  const [modalitaRegistrazione, setModalitaRegistrazione] =
    useState<'continua' | 'assistenza' | null>(null)
  const caricati = useRef(false)

  /**
   * Telefoni che restano connessi (521). Senza sessione, l'app Android la riapre dal telefono ricordato;
   * la web app dell'iPhone mostra lo sblocco con Face ID se il telefono ha la sua passkey. Una ripresa
   * alla volta: piu' richieste scadute insieme aspettano la stessa.
   */
  const modo = useMemo(() => modoTelefono(), [])
  const [bloccato, setBloccato] = useState(false)
  const [sblocco, setSblocco] = useState<OpzioniSblocco | null>(null)
  const ripresaInCorso = useRef<Promise<boolean> | null>(null)
  const riprendi = useCallback((): Promise<boolean> => {
    if (modo === 'app') {
      ripresaInCorso.current ??= riprendiApp().finally(() => { ripresaInCorso.current = null })
      return ripresaInCorso.current
    }
    if (modo === 'schermata_home') {
      return sfidaSblocco().then((o) => {
        if (o) { setSblocco(o); setBloccato(true) }
        return false
      }).catch(() => false)
    }
    return Promise.resolve(false)
  }, [modo])

  const aggiorna = useCallback(async () => {
    // La sola lettura dell'impersonazione (524) si imposta prima di passare `io` ai componenti: le loro
    // chiamate automatiche devono gia' trovarla.
    const leggi = async () => {
      const nuovo = await api.get<Io>('/auth/io')
      impostaSolaLettura(nuovo.impersonazione ? `${nuovo.nome} ${nuovo.cognome}` : null)
      setIo(nuovo)
    }
    try {
      try {
        await leggi()
      } catch (e) {
        if (!(e instanceof ErroreApi && e.stato === 401) || !await riprendi()) throw e
        await leggi()
      }
    } catch (e) {
      if (e instanceof ErroreApi && (e.stato === 401 || e.stato === 403)) { impostaSolaLettura(null); setIo(null) }
      else throw e
    } finally {
      setCaricamento(false)
    }
  }, [riprendi])

  useEffect(() => {
    gestisciSessioneScaduta(modo ? riprendi : null)
    return () => gestisciSessioneScaduta(null)
  }, [modo, riprendi])

  const sbloccato = useCallback(async () => {
    setBloccato(false); setSblocco(null)
    await aggiorna()
  }, [aggiorna])
  const entraConPassword = useCallback(() => {
    setBloccato(false); setSblocco(null); impostaSolaLettura(null); setIo(null)
  }, [])

  // Web app ricordata: dopo cinque minuti in secondo piano lo schermo si copre subito, poi Face ID.
  useEffect(() => {
    if (modo !== 'schermata_home' || !io?.telefono?.passkey) return
    let nascostaDa: number | null = null
    const cambio = () => {
      if (document.hidden) { nascostaDa = Date.now(); return }
      if (nascostaDa !== null && Date.now() - nascostaDa > BLOCCO_WEB_APP) {
        setBloccato(true)
        void riprendi()
      }
      nascostaDa = null
    }
    document.addEventListener('visibilitychange', cambio)
    return () => document.removeEventListener('visibilitychange', cambio)
  }, [modo, io?.telefono?.passkey, riprendi])

  useEffect(() => { void aggiorna() }, [aggiorna])

  /**
   * Ingresso completo: secondo fattore superato, password definitiva e, dove l'obbligo e' in vigore,
   * secondo fattore attivo (493). Prima di allora l'API risponde 403 a tutto tranne che all'accesso:
   * cataloghi, notifiche e telefono aspettano.
   */
  const entrato = !!io && !(io.mfaAttiva && !io.mfaVerificata) && !io.deveCambiarePassword
    && !io.secondoFattore?.daAttivareOra

  // Cataloghi e sedi cambiano di rado: si caricano una volta sola, dopo l'accesso.
  useEffect(() => {
    if (!entrato || caricati.current) return
    caricati.current = true
    void Promise.all([
      api.get<Tassonomia>('/riferimenti/tassonomia'),
      api.get<Cataloghi>('/riferimenti/cataloghi'),
      api.get<Paese[]>('/riferimenti/paesi'),
      api.get<SedeSessione[]>('/riferimenti/sedi'),
    ]).then(([t, c, p, s]) => { setTassonomia(t); setCataloghi(c); setPaesi(p); setSedi(inOrdineDiSede(s)) })
      .catch(() => { caricati.current = false })
  }, [entrato])

  const ricaricaCataloghi = useCallback(async () => {
    setCataloghi(await api.get<Cataloghi>('/riferimenti/cataloghi'))
  }, [])

  const aggiornaNotifiche = useCallback(async () => {
    try {
      const r = await api.get<{ da_leggere: number }>('/notifiche?solo_da_leggere=true&limite=1')
      setNotificheDaLeggere(r.da_leggere)
    } catch { /* una notifica non arrivata non deve rompere la pagina */ }
  }, [])

  /**
   * App per iPhone e Android (484, servizi/app-nativa.ts). Fuori dall'app non succede niente.
   * Il tocco su una notifica porta alla pagina degli avvisi, anche prima dell'accesso: la pagina
   * si apre appena entrati. Il telefono si registra a ogni accesso, e a ogni riapertura con la
   * sessione ancora valida, solo a ingresso completo (secondo fattore e password definitiva).
   */
  const vai = useNavigate()
  useEffect(() => {
    preparaAppNativa({ vai: (percorso) => vai(percorso), nuovaNotifica: () => { void aggiornaNotifiche() } })
  }, [vai, aggiornaNotifiche])
  // Impersonando (524) il telefono di chi impersona non si registra per le notifiche dell'altro.
  const impersonando = !!io?.impersonazione
  useEffect(() => {
    if (entrato && !impersonando) void attivaNotifichePush()
  }, [entrato, io?.id, impersonando])

  /**
   * Avvisi sullo schermo (2/11/2026, servizi/avvisi.ts): una richiesta al minuto porta le email e le
   * notifiche nuove e, con la stessa risposta, il conteggio della campanella. Prende il posto
   * dell'interrogazione di /notifiche. Niente LISTEN/NOTIFY sul database serverless: si chiede, e solo
   * mentre qualcuno guarda la pagina, o con le notifiche del computer accese finche' dura la sessione.
   *
   * Impersonando (524) niente avvisi: la campanella si aggiorna come prima, con /notifiche.
   */
  useIntervalloAttivo(() => { void aggiornaNotifiche() }, OGNI_MINUTO, entrato && impersonando)
  const vaiRif = useRef(vai)
  vaiRif.current = vai
  const aggiornaRif = useRef(aggiorna)
  aggiornaRif.current = aggiorna
  useEffect(() => {
    if (!entrato || impersonando || !io?.id) return
    return avviaNovita({
      utenteId: io.id,
      conteggio: setNotificheDaLeggere,
      vai: (percorso) => vaiRif.current(percorso),
      scaduta: () => { void aggiornaRif.current() },
    })
  }, [entrato, impersonando, io?.id])

  // Dopo un accesso completo: l'app ricorda il telefono da sola; la web app propone Face ID (521).
  const [propostaFaceId, setPropostaFaceId] = useState(false)
  // Una volta per ingresso: /auth/io si rilegge spesso, e ricordare di nuovo sostituirebbe il telefono.
  const ricordatoPer = useRef<string | null>(null)
  useEffect(() => {
    if (!entrato || !io || io.impersonazione || io.telefono?.passkey || (io.telefono && modo === 'app')) return
    if (ricordatoPer.current === io.id) return
    ricordatoPer.current = io.id
    if (modo === 'app') { void ricordaApp(); return }
    if (modo !== 'schermata_home') return
    let chiusa = false
    try { chiusa = sessionStorage.getItem(PROPOSTA_CHIUSA) === io.id } catch { /* senza storage si propone */ }
    if (chiusa) return
    void passkeyDisponibile().then(setPropostaFaceId)
  }, [entrato, io, modo])
  const chiudiPropostaFaceId = useCallback(() => {
    setPropostaFaceId(false)
    try { if (io) sessionStorage.setItem(PROPOSTA_CHIUSA, io.id) } catch { /* resta chiusa per la pagina */ }
  }, [io])

  /**
   * «Ricordami su questo computer» (531): dopo un accesso completo, in un browser di computer con una passkey
   * della piattaforma (Windows Hello, Touch ID). Non impersonando (524), non se il computer e' gia' ricordato
   * per questa persona, non per 30 giorni dopo «Non ora». Il computer gia' ricordato rimette il segnale che
   * fa offrire Windows Hello alla pagina di accesso.
   */
  const computer = useMemo(() => suComputer(), [])
  const [propostaComputer, setPropostaComputer] = useState(false)
  const utenteId = io?.id ?? null
  const impersonandoQui = !!io?.impersonazione
  const computerRicordato = io?.telefono?.tipo === 'computer' && io.telefono.passkey
  useEffect(() => {
    if (!entrato || !utenteId || impersonandoQui || !computer) { setPropostaComputer(false); return }
    if (computerRicordato) { segnaComputerRicordato(true); setPropostaComputer(false); return }
    if (propostaComputerRimandata(utenteId)) return
    let vivo = true
    void passkeyDisponibile().then((si) => { if (vivo) setPropostaComputer(si) })
    return () => { vivo = false }
  }, [entrato, utenteId, impersonandoQui, computer, computerRicordato])
  const chiudiPropostaComputer = useCallback((rimanda: boolean) => {
    setPropostaComputer(false)
    if (rimanda && utenteId) rimandaPropostaComputer(utenteId)
  }, [utenteId])

  /**
   * Registrazione dello schermo.
   *
   * Si chiede all'API se l'interruttore è acceso, e solo in quel caso si scarica il
   * registratore e si apre una registrazione. Se è spento — come è oggi, e come dovrebbe
   * restare finché non ci sono i presupposti dell'art. 4 — qui non succede niente e il
   * browser non scarica nemmeno una riga di quel codice.
   */
  useEffect(() => {
    // Con il secondo fattore da attivare (493) l'API rifiuta anche le rotte della registrazione.
    if (!io || (io.mfaAttiva && !io.mfaVerificata) || io.secondoFattore?.daAttivareOra) return
    let vivo = true
    void registrazione.leggiStato().then(async (stato) => {
      if (!vivo) return
      setPuoRegistrarePerAssistenza(stato.assistenza)
      if (!stato.continua) return
      const aperta = await registrazione.avvia('continua')
      if (!vivo) { void aperta?.ferma(); return }
      // L'avviso resta in testa alla pagina finché la registrazione gira: chi è
      // registrato lo deve vedere, non lo deve scoprire.
      if (aperta) { setAvvisoRegistrazione(stato.avvertenza); setModalitaRegistrazione('continua') }
    }).catch(() => undefined)
    return () => {
      vivo = false
      void registrazione.inCorso()?.ferma()
      setAvvisoRegistrazione(null)
      setModalitaRegistrazione(null)
    }
  }, [io])

  /**
   * Registrazione chiesta dall'operatore per farsi aiutare: la accende e la spegne lui,
   * e vale solo finché resta su quella pagina. Le regole di conservazione e di visione
   * sono le stesse della registrazione continua.
   */
  const registrazionePerAssistenza = useCallback(async (accendi: boolean) => {
    // La registrazione continua non si interrompe dall'interfaccia: se qualcuno potesse
    // spegnerla non sarebbe piu' la misura che il DPO ha autorizzato. Si ferma solo quella
    // che l'operatore ha acceso per farsi aiutare.
    if (!accendi) {
      if (registrazione.inCorso()?.modalita !== 'assistenza') return
      await registrazione.inCorso()?.ferma()
      setAvvisoRegistrazione(null)
      setModalitaRegistrazione(null)
      return
    }
    const aperta = await registrazione.avvia('assistenza')
    if (aperta) {
      setModalitaRegistrazione(aperta.modalita)
      setAvvisoRegistrazione(
        'Stai registrando lo schermo per farti assistere. La registrazione si ferma quando '
        + 'la interrompi o esci, e la rivede solo il Referente privacy (o il DPO), con '
        + 'un’approvazione a quattro occhi.')
    }
  }, [])

  const esci = useCallback(async () => {
    await registrazione.inCorso()?.ferma()
    // Prima della chiusura della sessione, che serve a togliere il telefono.
    await scollegaTelefono()
    await api.post('/auth/esci')
    caricati.current = false
    ricordatoPer.current = null
    impostaSolaLettura(null); setIo(null); setTassonomia(null); setCataloghi(null); setPaesi([]); setSedi([]); setNotificheDaLeggere(0)
  }, [])

  const valore = useMemo<Contesto>(() => ({
    io, caricamento, tassonomia, cataloghi, paesi, sedi, notificheDaLeggere, avvisoRegistrazione,
    modalitaRegistrazione, puoRegistrarePerAssistenza, registrazionePerAssistenza,
    aggiorna, aggiornaNotifiche, esci, ricaricaCataloghi,
    puo: (permesso) => !!io?.permessi.includes(permesso),
    modoTelefono: modo, bloccato, sblocco, sbloccato, entraConPassword, propostaFaceId, chiudiPropostaFaceId,
    propostaComputer, chiudiPropostaComputer,
  }), [io, caricamento, tassonomia, cataloghi, paesi, sedi, notificheDaLeggere, avvisoRegistrazione,
    modalitaRegistrazione, puoRegistrarePerAssistenza, registrazionePerAssistenza,
    aggiorna, aggiornaNotifiche, esci, ricaricaCataloghi,
    modo, bloccato, sblocco, sbloccato, entraConPassword, propostaFaceId, chiudiPropostaFaceId,
    propostaComputer, chiudiPropostaComputer])

  return <ContestoSessione.Provider value={valore}>{children}</ContestoSessione.Provider>
}

export function useSessione(): Contesto {
  const c = useContext(ContestoSessione)
  if (!c) throw new Error('useSessione va usato dentro FornitoreSessione')
  return c
}

/** Nome leggibile di una voce di catalogo, con ripiego sul codice. */
export function useCatalogo(nome: string) {
  const { cataloghi } = useSessione()
  return useMemo(() => {
    const voci = cataloghi?.[nome] ?? []
    const perCodice = new Map(voci.map((v) => [v.codice, v.nome]))
    return { voci, nomeDi: (codice: string | null | undefined) => (codice ? perCodice.get(codice) ?? codice : '') }
  }, [cataloghi, nome])
}

/** Nome italiano di un paese dal codice ISO. */
export function usePaesi() {
  const { paesi } = useSessione()
  return useMemo(() => {
    const perCodice = new Map(paesi.map((p) => [p.codice_iso3, p.nome]))
    return { paesi, nomeDi: (c: string | null | undefined) => (c ? perCodice.get(c) ?? c : '') }
  }, [paesi])
}

/** Colore dell'area, per le etichette. */
export function useAree() {
  const { tassonomia } = useSessione()
  return useMemo(() => {
    const perCodice = new Map((tassonomia?.aree ?? []).map((a) => [a.codice, a]))
    return {
      aree: tassonomia?.aree ?? [],
      coloreDi: (codice: string | null | undefined) => (codice ? perCodice.get(codice)?.colore ?? null : null),
      nomeDi: (codice: string | null | undefined) => (codice ? perCodice.get(codice)?.nome ?? codice : ''),
    }
  }, [tassonomia])
}
